/**
 * Image Compressor Component
 * Compress images with adjustable quality
 */

import { memo, useState, useCallback } from 'react';
import { useToast } from '../../context';
import { 
  compressImage, 
  formatFileSize,
  type CompressionResult 
} from '../../services/imageCompressorService';
import { analytics } from '../../services/analyticsService';

// Icons
const UploadIcon = memo(() => (
  <svg className="w-12 h-12 mx-auto mb-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
));
UploadIcon.displayName = 'UploadIcon';

const DownloadIcon = memo(() => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
));
DownloadIcon.displayName = 'DownloadIcon';

const SpinnerIcon = memo(() => (
  <svg className="w-8 h-8 animate-spin text-indigo-400" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
));
SpinnerIcon.displayName = 'SpinnerIcon';

export const ImageCompressor = memo(function ImageCompressor() {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [result, setResult] = useState<CompressionResult | null>(null);
  const [quality, setQuality] = useState(80);
  const [format, setFormat] = useState<'jpeg' | 'png' | 'webp'>('jpeg');
  const { success, error, info } = useToast();

  const processFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      error('Please upload an image file');
      return;
    }

    setIsProcessing(true);
    info('Compressing image...');

    try {
      // Store original
      const reader = new FileReader();
      reader.onload = (e) => setOriginalImage(e.target?.result as string);
      reader.readAsDataURL(file);
      setOriginalFile(file);

      // Compress
      const compressed = await compressImage(file, {
        quality: quality / 100,
        format,
      });

      setResult(compressed);
      
      if (compressed.savedPercentage > 0) {
        success(`Compressed! Saved ${compressed.savedPercentage}%`);
        analytics.compress.compress(file.size, compressed.blob.size, compressed.format);
      } else {
        info('Image is already optimized or format change increased size');
      }
    } catch (err) {
      error('Failed to compress image');
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  }, [quality, format, success, error, info]);

  const handleRecompress = useCallback(async () => {
    if (originalFile) {
      await processFile(originalFile);
    }
  }, [originalFile, processFile]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleDownload = useCallback(() => {
    if (!result || !originalFile) return;
    
    const url = URL.createObjectURL(result.blob);
    const link = document.createElement('a');
    link.href = url;
    const baseName = originalFile.name.replace(/\.[^/.]+$/, '');
    link.download = `${baseName}-compressed.${result.format === 'jpeg' ? 'jpg' : result.format}`;
    link.click();
    URL.revokeObjectURL(url);
    success('Downloaded!');
    analytics.compress.download(result.blob.size, result.format);
  }, [result, originalFile, success]);

  const handleClear = useCallback(() => {
    setOriginalImage(null);
    setOriginalFile(null);
    setResult(null);
  }, []);

  return (
    <div className="space-y-6">
      {/* Compression Settings */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
        <h3 className="text-sm font-medium text-slate-300 mb-4">Compression Settings</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs text-slate-400 mb-2">Quality: {quality}%</label>
            <input
              type="range"
              min="10"
              max="100"
              value={quality}
              onChange={(e) => setQuality(Number(e.target.value))}
              className="w-full accent-indigo-500"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>Smaller file</span>
              <span>Better quality</span>
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-2">Output Format</label>
            <div className="flex gap-2">
              {(['jpeg', 'png', 'webp'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFormat(f)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    format === f
                      ? 'bg-indigo-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {f.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
        {originalFile && (
          <button
            onClick={handleRecompress}
            disabled={isProcessing}
            className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            Apply New Settings
          </button>
        )}
      </div>

      {/* Drop Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          relative rounded-xl border-2 border-dashed p-8 text-center transition-all cursor-pointer
          ${isDragging ? 'border-indigo-500 bg-indigo-500/10' : 'border-slate-600 hover:border-indigo-400 bg-slate-800/50'}
          ${isProcessing ? 'pointer-events-none opacity-50' : ''}
        `}
      >
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={handleFileInput}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isProcessing}
        />
        
        {isProcessing ? (
          <div className="py-4">
            <SpinnerIcon />
            <p className="mt-4 text-slate-400">Compressing...</p>
          </div>
        ) : (
          <>
            <UploadIcon />
            <p className="text-slate-300 font-medium">Drop your image here</p>
            <p className="text-slate-500 text-sm mt-1">PNG, JPG, or WebP</p>
          </>
        )}
      </div>

      {/* Results */}
      {result && (
        <>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Original */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-400">Original</h3>
                <span className="text-sm text-slate-500">{formatFileSize(result.originalSize)}</span>
              </div>
              <div className="bg-slate-900 rounded-lg p-4 flex items-center justify-center min-h-[200px]">
                {originalImage && (
                  <img src={originalImage} alt="Original" className="max-w-full max-h-[250px] object-contain" />
                )}
              </div>
            </div>

            {/* Compressed */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-400">Compressed</h3>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">{formatFileSize(result.compressedSize)}</span>
                  {result.savedPercentage > 0 && (
                    <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                      -{result.savedPercentage}%
                    </span>
                  )}
                </div>
              </div>
              <div className="bg-slate-900 rounded-lg p-4 flex items-center justify-center min-h-[200px]">
                <img src={result.dataUrl} alt="Compressed" className="max-w-full max-h-[250px] object-contain" />
              </div>
            </div>
          </div>

          {/* Stats & Download */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-6 text-sm">
                <div>
                  <span className="text-slate-500">Original:</span>
                  <span className="text-white ml-2">{formatFileSize(result.originalSize)}</span>
                </div>
                <div className="text-2xl text-slate-600">→</div>
                <div>
                  <span className="text-slate-500">Compressed:</span>
                  <span className="text-green-400 ml-2 font-medium">{formatFileSize(result.compressedSize)}</span>
                </div>
                <div>
                  <span className="text-slate-500">Saved:</span>
                  <span className={`ml-2 font-medium ${result.savedPercentage > 0 ? 'text-green-400' : 'text-yellow-400'}`}>
                    {result.savedPercentage > 0 ? `${result.savedPercentage}%` : 'N/A'}
                  </span>
                </div>
              </div>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 rounded-lg font-medium transition-colors"
              >
                <DownloadIcon />
                Download Compressed
              </button>
            </div>
          </div>

          {/* Clear Button */}
          <div className="text-center">
            <button
              onClick={handleClear}
              className="px-6 py-2.5 text-sm font-medium bg-slate-700 hover:bg-slate-600 text-white rounded-lg border border-slate-600 hover:border-slate-500 transition-all"
            >
              Compress Another Image
            </button>
          </div>
        </>
      )}

      {/* Info */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <h4 className="text-sm font-medium text-slate-300 mb-2">💡 Tips:</h4>
        <ul className="text-sm text-slate-400 space-y-1 list-disc list-inside">
          <li>WebP offers the best compression for web images</li>
          <li>JPEG is ideal for photos, PNG for graphics with transparency</li>
          <li>Quality 70-80% usually gives good balance of size and quality</li>
          <li>All processing happens in your browser - files never leave your device</li>
        </ul>
      </div>
    </div>
  );
});

export default ImageCompressor;
